import {
  Box,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Spinner,
  useToast,
  useBreakpointValue,
  FormLabel,
  FormControl,
} from "@chakra-ui/react";
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from "react-query";

import { ButtonDefault } from "../../../components/ButtonDefault";
import { Input } from "../../../components/Form/Input";
import { Header } from "../../../components/Header";
import { Sidebar } from "../../../components/Sidebar";
import { api } from "../../../services/api";
import ContentBase from "../../../components/ContentBase";
import MainBase from "../../../components/MainBase";
import { TextAreaEditor } from "../../../components/TextAreaEditor";
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';

type TemplateData = {
  id: string;
  title: string;
  subject: string;
  body: string;
  version: number;
  template: string;
  is_active: boolean;
}

const updateTemplateFormSchema = Yup.object().shape({
  subject: Yup.string()
    .required('Assunto é obrigatório'),
})

export function UpdateTemplate() {
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [templateData, setTemplateData] = useState({} as TemplateData);
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());

  const onEditorStateChange = (editorStates: EditorState) => {
    setEditorState(editorStates);
  };

  const { id: templateId } = useParams<{ id: string }>();

  useEffect(() => {
    setIsLoading(true)
    try {
      api.get<TemplateData>(`templates/${templateId}`).then(
        response => {
          const { data } = response;

          const contentBlock = htmlToDraft(data.body);
          if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
            setEditorState(EditorState.createWithContent(contentState));
          }

          setTemplateData(data)
          setIsLoading(false)
        }
      );
    } catch (err: any) {
      toast({
        title: "Opa...",
        description: "Ocorreu uma instabilidade. Por favor tente novamente mais tarde.",
        status: "error",
        duration: 3000, // 3 seconds,
        isClosable: true,
        position: "top-right",
      })
      setIsLoading(false)
    }
  }, [toast, templateId])

  const updateTemplate = useMutation(async (template: TemplateData) => {
    const response = await api.post(`/templates`, template)

    return response.data.template;
  });

  const { register, handleSubmit, formState } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: yupResolver(updateTemplateFormSchema),
    context: { product_id: templateId }
  })

  const { errors } = formState

  const handleUpdateTemplate: SubmitHandler<TemplateData> = async (values) => {
    try {
      // Converter o editorState para HTML
      const bodyHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));

      const templateToUpdate = {
        ...values,
        body: bodyHtml,
        title: templateData.title,
        template: templateData.template
      };

      await updateTemplate.mutateAsync(templateToUpdate);

      toast({
        title: "Template alterado",
        description: "Alteração realizada com sucesso.",
        status: "success",
        duration: 3000, // 3 seconds,
        isClosable: true,
        position: "top-right",
      })

      window.close()
    } catch (err: any) {
      toast({
        title: "Opa...",
        description: "Ocorreu uma instabilidade. Por favor tente novamente mais tarde.",
        status: "error",
        duration: 3000, // 3 seconds,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  })

  return (
    <Flex direction="column" h="100vh">
      { !isWideVersion && <Header /> }
      <ContentBase>
        <Sidebar />
        <MainBase mainTitle="Alterar template">
          <Box
            as="form"
            h={["", "80vh"]}
            overflow="auto"
            paddingRight="1"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: "var(--chakra-colors-gray-400)",
                borderRadius: '25px',
              },
            }}
            onSubmit={handleSubmit(handleUpdateTemplate)}
          >
            {isLoading
              ? (
                <Flex justify="center">
                  <Spinner color="blue.900" size="xl" />
                </Flex>
              )
              : (
                  <VStack spacing="8" color="blue.900">
                    <SimpleGrid minChildWidth="248px" spacing={["6", "8"]} w="100%">
                      <Input
                        nameInput="title"
                        label="Título do template"
                        labelSize="lg"
                        error={errors.title}
                        defaultValue={templateData.title}
                        isDisabled={true}
                      />
                    </SimpleGrid>
                    <SimpleGrid minChildWidth="248px" spacing={["6", "8"]} w="100%">
                      <Input
                        nameInput="subject"
                        label="Assunto do template"
                        labelSize="lg"
                        isRequired
                        error={errors.subject}
                        defaultValue={templateData.subject}
                        {...register('subject')}
                      />
                    </SimpleGrid>
                <SimpleGrid minChildWidth="248px" spacing={["6", "8"]} w="100%">
                  <FormControl alignItems="center" isRequired>
                    <FormLabel htmlFor="link">
                      Corpo do template
                    </FormLabel>
                    <TextAreaEditor
                      editorState={editorState}
                      onEditorStateChange={onEditorStateChange}
                      editorStyle={{height: '300px'}}
                    />
                  </FormControl>
                </SimpleGrid>
                  </VStack>
              )
            }
            <Flex mt="8" justify="flex-end">
              <HStack spacing="4">
                <ButtonDefault
                  text="Cancelar"
                  w="150px"
                  info
                  isLoading={formState.isSubmitting}
                  onClick={() => window.close()}
                />
                <ButtonDefault
                  type="submit"
                  text="Salvar"
                  w="150px"
                  isLoading={formState.isSubmitting}
                />
              </HStack>
            </Flex>
          </Box>
        </MainBase>
      </ContentBase>
    </Flex>
  );
}
